import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeading } from '@/components/ui/section-heading';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useAuth } from '@/contexts/AuthContext';
import { CurriculumSubjectNavSyncContext } from "@/contexts/CurriculumSubjectNavSyncContext";
import { ArrowUp, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from '@/lib/utils';
import { getEvents, supabase } from "@/integrations/supabase/client";
import { endOfMonth, isAfter, startOfMonth } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MembersMediaTab from './components/MembersMediaTab';
import MembersMerchandiseTab from './components/MembersMerchandiseTab';
import MembersKoreanWordsTab from './components/MembersKoreanWordsTab';
import MembersUpcomingEventsTab from './components/MembersUpcomingEventsTab';
import MembersVideosTab from './components/MembersVideosTab';
import MembersFormsTab from './components/MembersFormsTab';
import LilDragonsTab from './components/LilDragonsTab';
import HoShinSul from './components/curriculum/HoShinSul';
import Innae from './components/curriculum/Innae';
import OneStep from './components/curriculum/OneStep';
import Taeguek from './components/curriculum/Taeguek';
import Sparring from './components/curriculum/Sparring';
import ChoongMoo from './components/curriculum/ChoongMoo';
import CurriculumNonCombatTheory from './components/curriculum/CurriculumNonCombatTheory';
import CurriculumMooshim from './components/curriculum/CurriculumMooshim';
import CurriculumMooGongRyu from './components/curriculum/CurriculumMooGongRyu';
import CurriculumSongMooKwan from './components/curriculum/CurriculumSongMooKwan';
import CurriculumKukkiwon from './components/curriculum/CurriculumKukkiwon';
import type { TrainingVideoTabBucket } from "./trainingVideoCurriculumTabs";
import {
  getTrainingVideoListLabel,
  subfolderAlbumToTabBucket,
  uncategorizedTrainingVideoTabBucket,
} from "./trainingVideoCurriculumTabs";

type ObjectMediaRow = Tables<"object_media">;
type MediaAlbumRow = Tables<"object_media_album">;

// Belt rank type and options
export type BeltRank = 'WHITE' | 'YELLOW' | 'ORANGE' | 'BLUE' | 'GREEN' | 'PURPLE' | 'BROWN' | 'RED' | 'NAVY' | 'BLACK/WHITE' | 'BLACK/D1' | 'BLACK/D2' | 'BLACK/D3' | 'BLACK/D4' | 'BLACK/D5';

const beltRanks: BeltRank[] = [
  'WHITE', 'YELLOW', 'ORANGE', 'BLUE', 'GREEN', 'PURPLE', 'BROWN', 'RED', 'NAVY', 'BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'
];

// Belt color mapping
const getBeltColor = (belt: BeltRank): string => {
  switch (belt) {
    case 'WHITE': return 'bg-white';
    case 'YELLOW': return 'bg-yellow-100';
    case 'ORANGE': return 'bg-orange-100';
    case 'BLUE': return 'bg-blue-100';
    case 'GREEN': return 'bg-green-100';
    case 'PURPLE': return 'bg-purple-100';
    case 'BROWN': return 'bg-amber-800 text-white';
    case 'RED': return 'bg-red-100';
    case 'NAVY': return 'bg-blue-900 text-white';
    case 'BLACK/WHITE': return 'bg-gray-300';
    case 'BLACK/D1': return 'bg-gray-700 text-white';
    case 'BLACK/D2': return 'bg-gray-800 text-white';
    case 'BLACK/D3': return 'bg-gray-900 text-white';
    case 'BLACK/D4': return 'bg-gray-900 text-white';
    case 'BLACK/D5': return 'bg-gray-900 text-white';
    default: return 'bg-white';
  }
};

/** Same belt tint on every curriculum sub-tab; use with `!` so Radix trigger defaults do not win. */
const getCurriculumTabBeltSurface = (belt: BeltRank): string => {
  switch (belt) {
    case 'WHITE':
      return '!bg-white !text-gray-900';
    case 'YELLOW':
      return '!bg-yellow-100 !text-gray-900';
    case 'ORANGE':
      return '!bg-orange-100 !text-gray-900';
    case 'BLUE':
      return '!bg-blue-100 !text-gray-900';
    case 'GREEN':
      return '!bg-green-100 !text-gray-900';
    case 'PURPLE':
      return '!bg-purple-100 !text-gray-900';
    case 'BROWN':
      return '!bg-amber-800 !text-white';
    case 'RED':
      return '!bg-red-100 !text-gray-900';
    case 'NAVY':
      return '!bg-blue-900 !text-white';
    case 'BLACK/WHITE':
      return '!bg-gray-300 !text-gray-900';
    case 'BLACK/D1':
      return '!bg-gray-700 !text-white';
    case 'BLACK/D2':
      return '!bg-gray-800 !text-white';
    case 'BLACK/D3':
    case 'BLACK/D4':
    case 'BLACK/D5':
      return '!bg-gray-900 !text-white';
    default:
      return '!bg-white !text-gray-900';
  }
};

// Oral Theory content
interface OralTheoryItem {
  verse: number;
  title: string;
  content: string;
  type: 'student_creed' | 'korean_count' | 'black_belt_cycle' | 'tenets' | 'black_belt_oath';
}

const oralTheoryContent: OralTheoryItem[] = [
  {
    verse: 1,
    title: "Student Creed Verse 1",
    content: "I intend to develop myself in a positive manner, and avoid anything that would reduce my mental growth, or my physical health.",
    type: 'student_creed'
  },
  {
    verse: 2,
    title: "Student Creed Verse 2",
    content: "I intend to develop self discipline, in order to bring out the best in myself, and others.",
    type: 'student_creed'
  },
  {
    verse: 3,
    title: "Student Creed Verse 3",
    content: "I intend to use what I learn in class constructively and defensively: To help myself and my fellow human being and never to be abusive or offensive.",
    type: 'student_creed'
  },
  {
    verse: 4,
    title: "Count 1-10 in Korean",
    content: "* 1: 하나 (hana)\n* 2: 둘 (dul)\n* 3: 셋 (set)\n* 4: 넷 (net)\n* 5: 다섯 (daseot)\n* 6: 여섯 (yeoseot)\n* 7: 일곱 (ilgob)\n* 8: 여덟 (yeodeol)\n* 9: 아홉 (ahop)\n* 10: 열 (yeol)",
    type: 'korean_count'
  },
  {
    verse: 5,
    title: "Black Belt Success Cycle",
    content: "* Know what you want\n* Have a plan\n* And a success coach\n* Take consistent action\n* Review your progress\n* Renew your goals",
    type: 'black_belt_cycle'
  },
  {
    verse: 6,
    title: "Tenets of TaeKwonDo",
    content: "* Courtesy\n* Integrity\n* Perseverance\n* Self-Control\n* Indomitable Spirit",
    type: 'tenets'
  },
  {
    verse: 7,
    title: "Black Belt Oath",
    content: "I accept with honour and privilege, the BLACK BELT in Martial Arts, and do hereby swear to uphold the standards of honesty, integrity, courtesy, high moral code and indomitable spirit.\n\nI sincerely pledge to honour my instructor and school with loyalty and support, and to value and carry on the Martial Arts tradition with respect and dignity.\n\nI further pledge, with humility and gratitude, to continue my mental, physical and spiritual growth to the highest level, to lead by example and to share what I have learned with others.",
    type: 'black_belt_oath'
  }
];

// Theory data structure for each belt
interface TheoryContent {
  written: {
    title: string;
    description: string;
  };
  oral: {
    currentVerse: number;
  };
  practical: {
    title: string;
    content: string;
  } | null;
  additionalTheory?: {
    title: string;
    content: string;
  }[];
}

// Theory content for each belt
const getTheoryContent = (belt: BeltRank): TheoryContent => {
  switch (belt) {
    case 'WHITE':
      return {
        written: {
          title: 'Shim Shin Soo Ryun',
          description: 'Training of the mind, body, spirit and emotion (1-2 pages. Under 10\'s can submit a drawing of the same subject)'
        },
        oral: {
          currentVerse: 1
        },
        practical: null
      };
    case 'YELLOW':
      return {
        written: {
          title: 'Jung Shin Tang Il',
          description: 'Concentration of the mind, body, spirit and emotion (1-2 pages. Under 10\'s can submit a drawing of the same subject)'
        },
        oral: {
          currentVerse: 2
        },
        practical: null
      };
    case 'ORANGE':
      return {
        written: {
          title: 'Kihap',
          description: 'Shout with concentration of energy (2-3 pages. Under 10\'s can submit a drawing of the same subject)'
        },
        oral: {
          currentVerse: 3
        },
        practical: null
      };
    case 'BLUE':
      return {
        written: {
          title: 'Moo Gong',
          description: 'Guardian of the peace (2-3 pages. Under 10\'s can submit a drawing)'
        },
        oral: {
          currentVerse: 4
        },
        practical: null
      };
    case 'GREEN':
      return {
        written: {
          title: 'Moo Do - Martial Art',
          description: 'The way of stopping a fight (2-3 pages. Under 10\'s can submit a drawing)'
        },
        oral: {
          currentVerse: 5
        },
        practical: {
          title: 'Meditation',
          content: '30 minutes meditation - Optional'
        }
      };
    case 'PURPLE':
      return {
        written: {
          title: 'TaeKwonDo',
          description: 'The art of hand and foot (2-3 pages. Under 10\'s can submit a drawing)'
        },
        oral: {
          currentVerse: 6
        },
        practical: {
          title: 'Meditation',
          content: '30 minutes meditation - Optional'
        }
      };
    case 'BROWN':
      return {
        written: {
          title: 'Instructor Guidelines',
          description: 'Write instructor guidelines (4-5 pages. Under 10\'s can submit a drawing)'
        },
        oral: {
          currentVerse: 6
        },
        practical: {
          title: 'Meditation',
          content: '1 hour meditation - Optional'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          }
        ]
      };
    case 'RED':
      return {
        written: {
          title: 'Senior Responsibility',
          description: 'Describe the responsibilities of a senior belt - Senior means high colour belt (4-5 pages. Under 10\'s can submit a drawing)'
        },
        oral: {
          currentVerse: 6
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '1 hour meditation\n1 Day fast - Optional'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          }
        ]
      };
    case 'NAVY':
      return {
        written: {
          title: 'History of OMAC & Supreme Grandmaster Choi (4-5 pages. Under 10\'s can submit a drawing)',
          description: 'Research and document the history of OMAC & Supreme Grandmaster Choi'
        },
        oral: {
          currentVerse: 6
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '2 hour meditation\n2 day fast  - Optional'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          },
          {
            title: 'History of OMAC',
            content: 'Detailed history of OMAC, including its founding principles, development, and contributions to martial arts.'
          },
          {
            title: 'Supreme Grandmaster Choi',
            content: 'Biography and contributions of Supreme Grandmaster Choi to martial arts and OMAC.'
          }
        ]
      };
    case 'BLACK/WHITE':
      return {
        written: {
          title: 'Black Belt Theory',
          description: 'Martial Arts & My Life as a Black Belt in OMAC (5-6 pages)'
        },
        oral: {
          currentVerse: 6
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '12 hour meditation (1 month prior to test)\n3 day fasting'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          },
          {
            title: 'History of OMAC',
            content: 'Detailed history of OMAC, including its founding principles, development, and contributions to martial arts.'
          },
          {
            title: 'Supreme Grandmaster Choi',
            content: 'Biography and contributions of Supreme Grandmaster Choi to martial arts and OMAC.'
          }
        ]
      };
    case 'BLACK/D1':
    case 'BLACK/D2':
      return {
        written: {
          title: belt === 'BLACK/D1' ? 'How do you practice Patience, Disciple, Respect and its impact & application in a daily life (6-10 pages)' :
                 'Black Belt & Leaders responsibility (No less than 6 pages)',
          description: belt === 'BLACK/D1' ? 'Advanced Black Belt Theory' : 'Advanced Black Belt Theory'
        },
        oral: {
          currentVerse: 7
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '12 hour meditation (1 month prior to test)\n3 day fasting'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          },
          {
            title: 'History of OMAC',
            content: 'Detailed history of OMAC, including its founding principles, development, and contributions to martial arts.'
          },
          {
            title: 'Supreme Grandmaster Choi',
            content: 'Biography and contributions of Supreme Grandmaster Choi to martial arts and OMAC.'
          }
        ]
      };
    case 'BLACK/D3':
    case 'BLACK/D4':
      return {
        written: {
          title: belt === 'BLACK/D3' ? 'How do you develop instructor skills & Instructors responsibilities (No less than 8 pages)' :
                 'Meaning of Masters & Martial Arts as Wholistic life practice (No less than 8 pages)',
          description: 'Advanced Black Belt Theory'
        },
        oral: {
          currentVerse: 7
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '12 hour meditation (1 month prior to test)\n4 day fasting'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          },
          {
            title: 'History of OMAC',
            content: 'Detailed history of OMAC, including its founding principles, development, and contributions to martial arts.'
          },
          {
            title: 'Supreme Grandmaster Choi',
            content: 'Biography and contributions of Supreme Grandmaster Choi to martial arts and OMAC.'
          }
        ]
      };
    case 'BLACK/D5':
      return {
        written: {
          title: '3 major development of MoogongRyu Martial arts: Human Development, Sports & Arts Development and Business Development (12-16 pages)',
          description: 'Advanced Black Belt Theory'
        },
        oral: {
          currentVerse: 7
        },
        practical: {
          title: 'Meditation & Fasting',
          content: '12 hour meditation (1 month prior to test)\n5 day fasting'
        },
        additionalTheory: [
          {
            title: 'History of TaeKwonDo',
            content: 'Comprehensive understanding of TaeKwonDo history, including its origins, development, and major figures.'
          },
          {
            title: 'Sparring Rules',
            content: 'Complete knowledge of competition rules, scoring system, and prohibited acts in TaeKwonDo sparring.'
          },
          {
            title: 'History of OMAC',
            content: 'Detailed history of OMAC, including its founding principles, development, and contributions to martial arts.'
          },
          {
            title: 'Supreme Grandmaster Choi',
            content: 'Biography and contributions of Supreme Grandmaster Choi to martial arts and OMAC.'
          }
        ]
      };
    default:
      return {
        written: {
          title: '',
          description: ''
        },
        oral: {
          currentVerse: 0
        },
        practical: null
      };
  }
};

// Get applicable oral verses for a given belt rank
const getApplicableOralVerses = (currentVerse: number) => {
  return oralTheoryContent.filter(verse => verse.verse <= currentVerse);
};

// Taeguek pattern data structure
interface TaeguekPattern {
  number: number;
  name: string;
  meaning: string;
}

const taeguekPatterns: TaeguekPattern[] = [
  { number: 1, name: "Il Jang", meaning: "Heaven & Light" },
  { number: 2, name: "Ee Jang", meaning: "Lake & Joyfulness" },
  { number: 3, name: "Sam Jang", meaning: "Fire & Sun" },
  { number: 4, name: "Sa Jang", meaning: "Thunder" },
  { number: 5, name: "Oh Jang", meaning: "Wind" },
  { number: 6, name: "Yuk Jang", meaning: "Water" },
  { number: 7, name: "Chil Jang", meaning: "Mountain" },
  { number: 8, name: "Pal Jang", meaning: "Earth" }
];

// Get patterns for each belt
const getTaeguekPatterns = (belt: BeltRank): { patterns: TaeguekPattern[], currentPattern: number } => {
  switch (belt) {
    case 'WHITE': return { patterns: [], currentPattern: 0 };
    case 'YELLOW': return { patterns: taeguekPatterns.slice(0, 1), currentPattern: 1 };
    case 'ORANGE': return { patterns: taeguekPatterns.slice(0, 2), currentPattern: 2 };
    case 'BLUE': return { patterns: taeguekPatterns.slice(0, 3), currentPattern: 3 };
    case 'GREEN': return { patterns: taeguekPatterns.slice(0, 4), currentPattern: 4 };
    case 'PURPLE': return { patterns: taeguekPatterns.slice(0, 5), currentPattern: 5 };
    case 'BROWN': return { patterns: taeguekPatterns.slice(0, 6), currentPattern: 6 };
    case 'RED': return { patterns: taeguekPatterns.slice(0, 7), currentPattern: 7 };
    case 'NAVY': return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 8 };
    case 'BLACK/WHITE': return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 0 };
    case 'BLACK/D1': return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 0 };
    case 'BLACK/D2': return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 0 };
    case 'BLACK/D3': return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 0 };
    case 'BLACK/D4':
    case 'BLACK/D5':
      return { patterns: taeguekPatterns.slice(0, 8), currentPattern: 0 };
    default: return { patterns: [], currentPattern: 0 };
  }
};

// Get Mooshim techniques for a given belt rank
const getMooshimTechniques = (belt: BeltRank): string[] => {
  const baseTechniques = ['Horse', 'Bear (Back)', 'Bull (Front)'];  // WHITE belt starts with these
  
  switch (belt) {
    case 'WHITE':
      return baseTechniques;
    case 'YELLOW':
      return [...baseTechniques, 'Cat'];
    case 'ORANGE':
      return [...baseTechniques, 'Cat', 'Tiger'];
    case 'BLUE':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane'];
    case 'GREEN':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle'];
    case 'PURPLE':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra'];
    case 'BROWN':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion'];
    case 'RED':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion', 'Mooshim In-Eum'];
    case 'NAVY':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion', 'Mooshim In-Eum', 'Mooshim In-Jang'];
    case 'BLACK/WHITE':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion', 'Mooshim In-Eum', 'Mooshim In-Jang'];
    case 'BLACK/D1':
    case 'BLACK/D2':
    case 'BLACK/D3':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion', 'Mooshim In-Eum', 'Mooshim In-Jang'];
    case 'BLACK/D4':
    case 'BLACK/D5':
      return [...baseTechniques, 'Cat', 'Tiger', 'Crane', 'Mantis', 'Eagle', 'Cobra', 'Scorpion', 'Mooshim In-Eum', 'Mooshim In-Jang'];
    default:
      return [];
  }
};

// Get Innae techniques for a given belt rank
const getInnaeTechniques = (belt: BeltRank): string[] => {
  switch (belt) {
    case 'WHITE':
      return ['1', '2', '3'];
    case 'YELLOW':
      return ['1', '2', '3', '4'];
    case 'ORANGE':
      return ['1', '2', '3', '4', '5'];
    case 'BLUE':
      return ['1', '2', '3', '4', '5', '6'];
    case 'GREEN':
      return ['1', '2', '3', '4', '5', '6', '7'];
    case 'PURPLE':
      return ['1', '2', '3', '4', '5', '6', '7', '8'];
    case 'BROWN':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
    case 'RED':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'Innae In-Eum'];
    case 'NAVY':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'Innae In-Eum', 'Innae In-Jang'];
    case 'BLACK/WHITE':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'Innae In-Eum', 'Innae In-Jang'];
    case 'BLACK/D1':
    case 'BLACK/D2':
    case 'BLACK/D3':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'Innae In-Eum', 'Innae In-Jang'];
    case 'BLACK/D4':
    case 'BLACK/D5':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'Innae In-Eum', 'Innae In-Jang'];
    default:
      return [];
  }
};


// Breaking techniques interface
interface BreakingTechniques {
  hand?: string;
  foot?: string;
  creative?: string;
}

// Get breaking techniques for each belt
const getBreakingTechniques = (belt: BeltRank): BreakingTechniques => {
  switch (belt) {
    case 'WHITE':
      return {
        hand: 'Knifehand strike',
        foot: 'Front kick'
      };
    case 'YELLOW':
      return {
        hand: 'Hammer fist',
        foot: 'Side kick'
      };
    case 'ORANGE':
      return {
        hand: 'Reverse knife hand',
        foot: 'Stepping Side kick'
      };
    case 'BLUE':
      return {
        hand: 'Elbow',
        foot: 'Axe kick'
      };
    case 'GREEN':
      return {
        hand: 'Palm Heel',
        foot: 'Back Kick'
      };
    case 'PURPLE':
      return {
        hand: 'Reverse Punch',
        foot: '1 step jump front kick'
      };
    case 'BROWN':
      return {
        hand: '1 x Students Choice',
        foot: 'Jump side kick'
      };
    case 'RED':
      return {
        hand: '2 x Students Choice',
        foot: 'Pop up side kick'
      };
    case 'NAVY':
      return {
        hand: 'N/A',
        foot: 'Reverse turning kick',
        creative: '2 moves, choice of hand and foot'
      };
    case 'BLACK/WHITE':
      return {
        creative: '3 - 4 stations - creative & concrete'
      };
    case 'BLACK/D1':
      return {
        creative: '4 - 5 stations - creative & concrete'
      };
    case 'BLACK/D2':
      return {
        creative: '5 - 6 stations - creative & concrete'
      };
    case 'BLACK/D3':
      return {
        creative: 'Students choice of creative breaking & concrete'
      };
    case 'BLACK/D4':
    case 'BLACK/D5':
      return {
        creative: 'Students choice of creative breaking & concrete'
      };
    default:
      return {};
  }
};

// Get kicks for each belt level
const getKicks = (belt: BeltRank): string[] => {
  const baseKicks = ['Front', 'Side', 'Round'];  // WHITE and YELLOW start with these
  
  switch (belt) {
    case 'WHITE':
    case 'YELLOW':
      return baseKicks;
    case 'ORANGE':
      return [...baseKicks, 'Axe'];
    case 'BLUE':
      return [...baseKicks, 'Axe', 'Back'];
    case 'GREEN':
      return [...baseKicks, 'Axe', 'Back'];
    case 'PURPLE':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent'];
    case 'BROWN':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook'];
    case 'RED':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting'];
    case 'NAVY':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning'];
    case 'BLACK/WHITE':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick"];
    case 'BLACK/D1':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick", "360' Jump Kicks"];
    case 'BLACK/D2':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick", "360' Jump Kicks"];
    case 'BLACK/D3':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick", "360' Jump Kicks"];
    case 'BLACK/D4':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick", "360' Jump Kicks"];
    case 'BLACK/D5':
      return [...baseKicks, 'Axe', 'Back', 'Inner Crescent', 'Hook', 'Twisting', 'Reverse Turning', 'One-step Jump', "180' Spin", "360 Spin", "Pop-up", "Jump", "360' Jump Back Kick", "360' Jump Kicks"];
    default:
      return [];
  }
};

// Get one-step requirements for each belt
const getOneStepRequirements = (belt: BeltRank): string => {
  switch (belt) {
    case 'WHITE':
      return '1-5';
    case 'YELLOW':
      return '1-5 Refined';
    case 'ORANGE':
      return '1-10';
    case 'BLUE':
      return '1-10 Refined';
    case 'GREEN':
      return '6-15';
    case 'PURPLE':
      return '6-15 Refined';
    case 'BROWN':
      return '11-20';
    case 'RED':
      return '11-20 Refined';
    case 'NAVY':
      return '16-25 + 5 Creative';
    case 'BLACK/WHITE':
    case 'BLACK/D1':
      return '16-25 + 15 Creative';
    case 'BLACK/D2':
      return '16-25 + 20 Creative';
    case 'BLACK/D3':
      return '16-25 + 10 Creative';
    case 'BLACK/D4':
    case 'BLACK/D5':
      return '16-25 + 10 Creative';
    default:
      return 'Not applicable';
  }
};

// Get Ho Shin Sul techniques for each belt
const getHoShinSulTechniques = (belt: BeltRank): number[] => {
  switch (belt) {
    case 'WHITE':
      return [1];
    case 'YELLOW':
      return [1, 2];
    case 'ORANGE':
      return [1, 2, 3];
    case 'BLUE':
      return [1, 2, 3, 4];
    case 'GREEN':
      return [1, 2, 3, 4, 5];
    case 'PURPLE':
      return [1, 2, 3, 4, 5, 6];
    case 'BROWN':
      return [1, 2, 3, 4, 5, 6, 7];
    case 'RED':
      return [1, 2, 3, 4, 5, 6, 7, 8];
    case 'NAVY':
      return [1, 2, 3, 4, 5, 6, 7, 8];
    case 'BLACK/WHITE':
      return [1, 2, 3, 4, 5, 6, 7, 8];
    case 'BLACK/D1':
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    case 'BLACK/D2':
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    case 'BLACK/D3':
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    case 'BLACK/D4':
    case 'BLACK/D5':
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    default:
      return [];
  }
};

// DaeMahngChun pattern data structure
interface DaeMahngChunPattern {
  number: number;
  name: string;
  meaning: string;
}

const daeMahngChunPatterns: DaeMahngChunPattern[] = [
  { number: 1, name: "DaeMahngChun 1", meaning: "Big Dream Heaven 1" },
  { number: 2, name: "DaeMahngChun 2", meaning: "Big Dream Heaven 2" },
  { number: 3, name: "DaeMahngChun 3", meaning: "Big Dream Heaven 3" },
  { number: 4, name: "DaeMahngChun 4", meaning: "Big Dream Heaven 4" },
  { number: 5, name: "DaeMahngChun 5", meaning: "Big Dream Heaven 5" },
  { number: 6, name: "DaeMahngChun 6", meaning: "Big Dream Heaven 6" },
  { number: 7, name: "DaeMahngChun 7", meaning: "Big Dream Heaven 7" }
];

// Get DaeMahngChun patterns for each belt
const getDaeMahngChunPatterns = (belt: BeltRank): { patterns: DaeMahngChunPattern[], currentPattern: number } => {
  switch (belt) {
    case 'WHITE': return { patterns: daeMahngChunPatterns.slice(0, 1), currentPattern: 1 };
    case 'YELLOW': return { patterns: daeMahngChunPatterns.slice(0, 2), currentPattern: 2 };
    case 'ORANGE': return { patterns: daeMahngChunPatterns.slice(0, 3), currentPattern: 3 };
    case 'BLUE': return { patterns: daeMahngChunPatterns.slice(0, 4), currentPattern: 4 };
    case 'GREEN': return { patterns: daeMahngChunPatterns.slice(0, 5), currentPattern: 5 };
    case 'PURPLE': return { patterns: daeMahngChunPatterns.slice(0, 6), currentPattern: 6 };
    case 'BROWN': return { patterns: daeMahngChunPatterns.slice(0, 7), currentPattern: 7 };
    case 'RED':
    case 'NAVY':
    case 'BLACK/WHITE':
    case 'BLACK/D1':
    case 'BLACK/D2':
    case 'BLACK/D3':
    case 'BLACK/D4':
    case 'BLACK/D5':
      return { patterns: daeMahngChunPatterns.slice(0, 7), currentPattern: 0 };
    default: return { patterns: [], currentPattern: 0 };
  }
};

// DaeMahngJi pattern data structure
interface DaeMahngJiPattern {
  number: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  name: string;
  meaning: string;
}

const daeMahngJiPatterns: DaeMahngJiPattern[] = [
  { number: 1, name: "DaeMahngJi 1", meaning: "Big Dream Earth 1" },
  { number: 2, name: "DaeMahngJi 2", meaning: "Big Dream Earth 2" },
  { number: 3, name: "DaeMahngJi 3", meaning: "Big Dream Earth 3" },
  { number: 4, name: "DaeMahngJi 4", meaning: "Big Dream Earth 4" },
  { number: 5, name: "DaeMahngJi 5", meaning: "Big Dream Earth 5" },
  { number: 6, name: "DaeMahngJi 6", meaning: "Big Dream Earth 6" },
  { number: 7, name: "DaeMahngJi 7", meaning: "Big Dream Earth 7" }
];

// Get DaeMahngJi patterns for each belt
const getDaeMahngJiPatterns = (belt: BeltRank): { patterns: DaeMahngJiPattern[], currentPattern: number } => {
  switch (belt) {
    case 'GREEN': return { patterns: daeMahngJiPatterns.slice(0, 1), currentPattern: 1 };
    case 'PURPLE': return { patterns: daeMahngJiPatterns.slice(0, 2), currentPattern: 2 };
    case 'BROWN': return { patterns: daeMahngJiPatterns.slice(0, 3), currentPattern: 3 };
    case 'RED': return { patterns: daeMahngJiPatterns.slice(0, 4), currentPattern: 4 };
    case 'NAVY': return { patterns: daeMahngJiPatterns.slice(0, 5), currentPattern: 5 };
    case 'BLACK/WHITE': return { patterns: daeMahngJiPatterns.slice(0, 6), currentPattern: 6 };
    case 'BLACK/D1': return { patterns: daeMahngJiPatterns.slice(0, 7), currentPattern: 7 };
    case 'BLACK/D2':
    case 'BLACK/D3':
    case 'BLACK/D4':
    case 'BLACK/D5':
      return { patterns: daeMahngJiPatterns.slice(0, 7), currentPattern: 0 };
    default: return { patterns: [], currentPattern: 0 };
  }
};

// Add visibility check functions
const isTaeguekVisible = (selectedBelt: BeltRank) => {
  const { patterns } = getTaeguekPatterns(selectedBelt);
  return patterns.length > 0;
};

const isDaeMahngChunVisible = (selectedBelt: BeltRank) => {
  const { patterns } = getDaeMahngChunPatterns(selectedBelt);
  return patterns.length > 0;
};

const isDaeMahngJiVisible = (selectedBelt: BeltRank) => {
  const { patterns } = getDaeMahngJiPatterns(selectedBelt);
  return patterns.length > 0;
};

const isChoshimVisible = (selectedBelt: BeltRank) => {
  return ['NAVY', 'BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isKoryoVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isKeumgangVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isGyeomJeonVisible = (selectedBelt: BeltRank) => {
  return ['NAVY','BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isRohaiVisible = (selectedBelt: BeltRank) => {
  return ['NAVY','BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isChulGiVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isYeonBiVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isPalBahngMooGongVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

// Add visibility checks for advanced patterns
const isAhmHakVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isKeumKangKwonVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isTaebaekVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isPyongWonVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isSoHoYeonVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isSipJinVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isJitaeVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D5'].includes(selectedBelt);
};

const isSaengMyongYangVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D5'].includes(selectedBelt);
};

const isSaengMyongEumVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isSongMooVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D5'].includes(selectedBelt);
};

const isJaEunVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isBalSaeDaeVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/WHITE', 'BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isJoHwaVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isChoongMooVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

const isBalSeakSoVisible = (selectedBelt: BeltRank) => {
  return ['BLACK/D1', 'BLACK/D2', 'BLACK/D3', 'BLACK/D4', 'BLACK/D5'].includes(selectedBelt);
};

function getDirectChildFolderOfTraining(
  taggedAlbumId: string,
  trainingRootId: string,
  albumById: Map<string, MediaAlbumRow>,
): MediaAlbumRow | null {
  if (taggedAlbumId === trainingRootId) return null;
  let current = albumById.get(taggedAlbumId);
  if (!current) return null;
  for (;;) {
    if (!current.parent_id) return null;
    const parent = albumById.get(current.parent_id);
    if (!parent) return null;
    if (parent.id === trainingRootId) return current;
    current = parent;
  }
}

function trainingTabBucketIsBetter(a: TrainingVideoTabBucket, b: TrainingVideoTabBucket): boolean {
  if (a.sortIndex !== b.sortIndex) return a.sortIndex < b.sortIndex;
  if (a.tabKey !== b.tabKey) return a.tabKey < b.tabKey;
  return a.tabLabel < b.tabLabel;
}

export default function MembersArea() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedBelt, setSelectedBelt] = useState<BeltRank>('WHITE');
  const [trainingVideos, setTrainingVideos] = useState<ObjectMediaRow[]>([]);
  const [trainingVideoTabBucketByMediaId, setTrainingVideoTabBucketByMediaId] = useState<
    Record<string, TrainingVideoTabBucket>
  >({});
  const [isLoadingTrainingVideos, setIsLoadingTrainingVideos] = useState(false);
  const [trainingVideosError, setTrainingVideosError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState<ObjectMediaRow | null>(null);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const mobileTabsScrollRef = useRef<HTMLDivElement | null>(null);
  const beltPickerScrollRef = useRef<HTMLDivElement | null>(null);
  /** Preserved when leaving Curriculum — Radix unmounts inactive TabsContent and resets scroll. */
  const beltPickerScrollLeftRef = useRef(0);
  /** Same pattern as belt picker: subject strip inside Curriculum. */
  const curriculumNavScrollLeftRef = useRef(0);
  /** Mobile members tab strip — persists across main tab switches. */
  const mobileTabsScrollLeftRef = useRef(0);
  const [showMobileTabsLeftHint, setShowMobileTabsLeftHint] = useState(false);
  const [showMobileTabsRightHint, setShowMobileTabsRightHint] = useState(false);
  const [showBeltPickerLeftHint, setShowBeltPickerLeftHint] = useState(false);
  const [showBeltPickerRightHint, setShowBeltPickerRightHint] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  /** Remaining (not ended) events in the current calendar month — shown on Events tab when count > 0. */
  const [remainingEventsThisMonthCount, setRemainingEventsThisMonthCount] = useState<number | null>(null);
  const [activeCurriculumCategory, setActiveCurriculumCategory] = useState("mooshim");
  const curriculumNavScrollRef = useRef<HTMLDivElement | null>(null);
  const [showCurriculumNavLeftHint, setShowCurriculumNavLeftHint] = useState(false);
  const [showCurriculumNavRightHint, setShowCurriculumNavRightHint] = useState(false);

  const { patterns, currentPattern } = getTaeguekPatterns(selectedBelt);
  const { patterns: dmcPatterns, currentPattern: currentDMC } = getDaeMahngChunPatterns(selectedBelt);
  const { patterns: dmjPatterns, currentPattern: currentDMJ } = getDaeMahngJiPatterns(selectedBelt);
  const theoryContent = getTheoryContent(selectedBelt);
  const applicableOralVerses = getApplicableOralVerses(theoryContent.oral.currentVerse);
  const memberTabs = [
    { value: "curriculum", label: "Curriculum", mobileLabel: "Curriculum" },
    { value: "lil-dragons", label: "Lil Dragons", mobileLabel: "Lil Dragons" },
    { value: "upcoming-events", label: "Upcoming Events", mobileLabel: "Events" },
    { value: "videos", label: "Videos", mobileLabel: "Videos" },
    { value: "media", label: "Media", mobileLabel: "Media" },
    { value: "merchandise", label: "Merchandise", mobileLabel: "Shop" },
    { value: "korean-words", label: "Korean Words", mobileLabel: "Korean" },
    { value: "forms", label: "Forms", mobileLabel: "Forms" },
  ] as const;
  const beltOptions = [
    { value: 'WHITE', image: '/images/belts/white_belt.png', label: 'White', badge: null },
    { value: 'YELLOW', image: '/images/belts/yellow_belt.png', label: 'Yellow', badge: null },
    { value: 'ORANGE', image: '/images/belts/orange_belt.png', label: 'Orange', badge: null },
    { value: 'BLUE', image: '/images/belts/light-blue_belt.png', label: 'Blue', badge: null },
    { value: 'GREEN', image: '/images/belts/green_belt.png', label: 'Green', badge: null },
    { value: 'PURPLE', image: '/images/belts/purple_belt.png', label: 'Purple', badge: null },
    { value: 'BROWN', image: '/images/belts/brown_belt.png', label: 'Brown', badge: null },
    { value: 'RED', image: '/images/belts/red_belt.png', label: 'Red', badge: null },
    { value: 'NAVY', image: '/images/belts/navy_belt.png', label: 'Navy', badge: null },
    { value: 'BLACK/WHITE', image: '/images/belts/grey_belt.png', label: 'Black/White', badge: 'BW' },
    { value: 'BLACK/D1', image: '/images/belts/black_belt.png', label: 'Degree 1', badge: 'D1' },
    { value: 'BLACK/D2', image: '/images/belts/black_belt.png', label: 'Degree 2', badge: 'D2' },
    { value: 'BLACK/D3', image: '/images/belts/black_belt.png', label: 'Degree 3', badge: 'D3' },
    { value: 'BLACK/D4', image: '/images/belts/black_belt.png', label: 'Degree 4', badge: 'D4' },
    { value: 'BLACK/D5', image: '/images/belts/black_belt.png', label: 'Degree 5', badge: 'D5' },
  ] as const satisfies ReadonlyArray<{
    value: BeltRank;
    image: string;
    label: string;
    badge: string | null;
  }>;

  const updateMobileTabOverflowHints = useCallback(() => {
    const container = mobileTabsScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowMobileTabsLeftHint(container.scrollLeft > 4);
    setShowMobileTabsRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  const updateBeltPickerOverflowHints = useCallback(() => {
    const container = beltPickerScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowBeltPickerLeftHint(container.scrollLeft > 4);
    setShowBeltPickerRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  const updateCurriculumNavOverflowHints = useCallback(() => {
    const container = curriculumNavScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowCurriculumNavLeftHint(container.scrollLeft > 4);
    setShowCurriculumNavRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  /** Step the horizontal tab strip (used by chevron overlay buttons). Clamped so we never scroll past the last item. */
  const scrollHorizontalTabStrip = useCallback(
    (stripRef: RefObject<HTMLDivElement | null>, direction: "backward" | "forward") => {
      const el = stripRef.current;
      if (!el) return;
      const delta = Math.max(160, Math.round(el.clientWidth * 0.65));
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === "backward"
          ? Math.max(0, el.scrollLeft - delta)
          : Math.min(maxScrollLeft, el.scrollLeft + delta);
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    },
    [],
  );

  /**
   * When nested curriculum tab strips scroll or change, keep the active "Select Subject"
   * tab centered in its horizontal strip.
   */
  const scrollActiveCurriculumSubjectIntoView = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      curriculumNavScrollRef.current,
      '[data-curriculum-subject-tab][data-state="active"]',
      () => updateCurriculumNavOverflowHints(),
    );
  }, [updateCurriculumNavOverflowHints]);

  const visibleCurriculumCategories = useMemo(() => {
    const b = selectedBelt;
    const isMooGongRyuAny =
      isDaeMahngChunVisible(b) ||
      isDaeMahngJiVisible(b) ||
      isChoshimVisible(b) ||
      isJoHwaVisible(b) ||
      isGyeomJeonVisible(b) ||
      isPalBahngMooGongVisible(b) ||
      isSongMooVisible(b) ||
      isSaengMyongEumVisible(b) ||
      isSaengMyongYangVisible(b);
    const isSongMooKwanAny =
      isChulGiVisible(b) ||
      isRohaiVisible(b) ||
      isBalSaeDaeVisible(b) ||
      isBalSeakSoVisible(b) ||
      isYeonBiVisible(b) ||
      isAhmHakVisible(b) ||
      isSoHoYeonVisible(b) ||
      isJaEunVisible(b) ||
      isKeumKangKwonVisible(b);
    const isKukkiwonAny =
      isKoryoVisible(b) ||
      isKeumgangVisible(b) ||
      isTaebaekVisible(b) ||
      isPyongWonVisible(b) ||
      isSipJinVisible(b) ||
      isJitaeVisible(b);

    const defs = [
      { value: "mooshim", label: "Mooshim", mobileLabel: "Mooshim", visible: true },
      { value: "innae", label: "Innae", mobileLabel: "Innae", visible: true },
      {
        value: "moogongryu",
        label: "Moo Gong Ryu",
        mobileLabel: "Moo Gong Ryu",
        visible: isMooGongRyuAny,
      },
      {
        value: "taegeuk",
        label: "Taegeuk",
        mobileLabel: "Taegeuk",
        visible: isTaeguekVisible(b),
      },
      {
        value: "kukkiwon-wt",
        label: "Kukkiwon / WT",
        mobileLabel: "Kukkiwon / WT",
        visible: isKukkiwonAny,
      },
      {
        value: "songmookwan",
        label: "Song Moo Kwan",
        mobileLabel: "Song Moo Kwan",
        visible: isSongMooKwanAny,
      },
      {
        value: "choongmoo",
        label: "Choong Moo",
        mobileLabel: "Choong Moo",
        visible: isChoongMooVisible(b),
      },
      { value: "one-step", label: "One-Step", mobileLabel: "One-Step", visible: true },
      { value: "ho-shin-sul", label: "Ho Shin Sul", mobileLabel: "Ho Shin Sul", visible: true },
      { value: "geukki", label: "Geukki", mobileLabel: "Geukki", visible: true },
      {
        value: "sparring",
        label: "Sparring/Breaking",
        mobileLabel: "Spar/Break",
        visible: true,
      },
      { value: "non-combat", label: "Theory/Other", mobileLabel: "Theory/Other", visible: true },
    ] as const;

    return defs.filter((d) => d.visible);
  }, [selectedBelt]);

  const fetchRemainingEventsThisMonthCount = useCallback(async () => {
    const now = new Date();
    const start = startOfMonth(now);
    const end = endOfMonth(now);
    const { data, error } = await getEvents(start, end);
    if (error || !data) {
      setRemainingEventsThisMonthCount(0);
      return;
    }
    const remaining = data.filter((e) => isAfter(new Date(e.end_time), now));
    setRemainingEventsThisMonthCount(remaining.length);
  }, []);

  const findTrainingVideoByAliases = useCallback(
    (aliases: string[]): ObjectMediaRow | null => {
      if (!trainingVideos || trainingVideos.length === 0) return null;
      const lowered = aliases.map((a) => a.toLowerCase());

      // First, try to find an exact base-name match (before file extension)
      const exactMatch =
        trainingVideos.find((video) => {
          const rawName = (video.name ?? video.url.split("/").pop() ?? "").toLowerCase();
          const baseName = rawName.replace(/\.[a-z0-9]+$/, "");
          return lowered.some((alias) => baseName === alias);
        }) ?? null;

      if (exactMatch) return exactMatch;

      // Fallback: looser "includes" match as before (for combos, summaries, etc.)
      return (
        trainingVideos.find((video) => {
          const name = (video.name ?? video.url.split("/").pop() ?? "").toLowerCase();
          return lowered.some((alias) => name.includes(alias));
        }) ?? null
      );
    },
    [trainingVideos],
  );

  const hasTrainingVideo = useCallback(
    (aliases: string[]) => !!findTrainingVideoByAliases(aliases),
    [findTrainingVideoByAliases],
  );

  const handleWatchTrainingVideo = useCallback(
    (aliases: string[]) => {
      const video = findTrainingVideoByAliases(aliases);
      if (!video) return;
      setActiveVideo(video);
      setIsVideoDialogOpen(true);
    },
    [findTrainingVideoByAliases],
  );

  useEffect(() => {
    const fetchTrainingVideos = async () => {
      try {
        setIsLoadingTrainingVideos(true);
        setTrainingVideosError(null);
        setTrainingVideoTabBucketByMediaId({});

        const { data: allAlbums, error: albumsError } = await supabase
          .from("object_media_album")
          .select("id, parent_id, album_name, album_string")
          .eq("archived", false);

        if (albumsError) {
          console.error("Error loading albums for training videos:", albumsError);
          setTrainingVideosError("There was a problem loading training videos.");
          return;
        }

        const albumRows = (allAlbums as MediaAlbumRow[]) ?? [];
        const trainingRoot = albumRows.find((a) => a.album_name === "Training");
        if (!trainingRoot) {
          setTrainingVideos([]);
          return;
        }

        const trainingRootId = trainingRoot.id;
        const subtreeIds = new Set<string>();
        const collectSubtree = (rootId: string) => {
          subtreeIds.add(rootId);
          for (const a of albumRows) {
            if (a.parent_id === rootId) collectSubtree(a.id);
          }
        };
        collectSubtree(trainingRootId);

        const albumById = new Map(albumRows.map((a) => [a.id, a]));

        const { data: tags, error: tagsError } = await (supabase as any)
          .from("object_media_tagged")
          .select("om_id, oma_id")
          .in("oma_id", Array.from(subtreeIds))
          .eq("archived", false);

        if (tagsError) {
          console.error("Error loading training tags:", tagsError);
          setTrainingVideosError("There was a problem loading training videos.");
          return;
        }

        const tagRows = (tags as { om_id: string; oma_id: string }[]) ?? [];
        const tagsByOm = new Map<string, string[]>();
        for (const t of tagRows) {
          const list = tagsByOm.get(t.om_id);
          if (list) list.push(t.oma_id);
          else tagsByOm.set(t.om_id, [t.oma_id]);
        }

        const mediaIds = [...new Set(tagRows.map((t) => t.om_id))];
        if (mediaIds.length === 0) {
          setTrainingVideos([]);
          return;
        }

        const { data: media, error: mediaError } = await supabase
          .from("object_media")
          .select("*")
          .in("id", mediaIds)
          .eq("archived", false)
          .eq("is_video", true)
          .order("create_time", { ascending: true });

        if (mediaError) {
          console.error("Error loading training videos:", mediaError);
          setTrainingVideosError("There was a problem loading training videos.");
          return;
        }

        const mediaList = (media as ObjectMediaRow[]) ?? [];
        const bucketByOm: Record<string, TrainingVideoTabBucket> = {};

        for (const row of mediaList) {
          const omaIds = tagsByOm.get(row.id) ?? [];
          let best: TrainingVideoTabBucket | null = null;

          for (const omaId of omaIds) {
            if (omaId === trainingRootId) {
              continue;
            }
            const taggedAlbum = albumById.get(omaId);
            if (!taggedAlbum) continue;
            const directChild = getDirectChildFolderOfTraining(
              taggedAlbum.id,
              trainingRootId,
              albumById,
            );
            if (!directChild) continue;
            const bucket = subfolderAlbumToTabBucket(directChild);
            if (!best || trainingTabBucketIsBetter(bucket, best)) best = bucket;
          }

          if (best) {
            bucketByOm[row.id] = best;
          } else {
            bucketByOm[row.id] = uncategorizedTrainingVideoTabBucket();
          }
        }

        setTrainingVideoTabBucketByMediaId(bucketByOm);
        setTrainingVideos(mediaList);
      } catch (error) {
        console.error("Unexpected error loading training videos:", error);
        setTrainingVideosError("There was a problem loading training videos.");
      } finally {
        setIsLoadingTrainingVideos(false);
      }
    };

    // Fetch once when component mounts
    fetchTrainingVideos();
  }, []);

  useEffect(() => {
    void fetchRemainingEventsThisMonthCount();
  }, [fetchRemainingEventsThisMonthCount]);

  useEffect(() => {
    if (activeTab === 'upcoming-events') {
      void fetchRemainingEventsThisMonthCount();
    }
  }, [activeTab, fetchRemainingEventsThisMonthCount]);

  useEffect(() => {
    updateMobileTabOverflowHints();
    updateBeltPickerOverflowHints();
    updateCurriculumNavOverflowHints();

    const handleResize = () => {
      updateMobileTabOverflowHints();
      updateBeltPickerOverflowHints();
      updateCurriculumNavOverflowHints();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    activeTab,
    activeCurriculumCategory,
    selectedBelt,
    updateBeltPickerOverflowHints,
    updateCurriculumNavOverflowHints,
    updateMobileTabOverflowHints,
    visibleCurriculumCategories,
  ]);

  useLayoutEffect(() => {
    if (
      visibleCurriculumCategories.some((c) => c.value === activeCurriculumCategory)
    ) {
      return;
    }
    setActiveCurriculumCategory(visibleCurriculumCategories[0]?.value ?? "mooshim");
    // User may have scrolled the subject strip to a tab that no longer exists for this belt;
    // selection jumps to the first subject — align the strip so that tab is visible.
    const navEl = curriculumNavScrollRef.current;
    if (navEl) {
      navEl.scrollLeft = 0;
      curriculumNavScrollLeftRef.current = 0;
    }
    updateCurriculumNavOverflowHints();
    requestAnimationFrame(() => {
      updateCurriculumNavOverflowHints();
    });
  }, [
    activeCurriculumCategory,
    visibleCurriculumCategories,
    updateCurriculumNavOverflowHints,
  ]);

  const handleMembersTabChange = useCallback(
    (next: string) => {
      if (mobileTabsScrollRef.current) {
        mobileTabsScrollLeftRef.current = mobileTabsScrollRef.current.scrollLeft;
      }
      if (activeTab === 'curriculum' && next !== 'curriculum') {
        if (beltPickerScrollRef.current) {
          beltPickerScrollLeftRef.current = beltPickerScrollRef.current.scrollLeft;
        }
        if (curriculumNavScrollRef.current) {
          curriculumNavScrollLeftRef.current = curriculumNavScrollRef.current.scrollLeft;
        }
      }
      setActiveTab(next);
    },
    [activeTab],
  );

  useLayoutEffect(() => {
    const restoreMobile = () => {
      const el = mobileTabsScrollRef.current;
      if (!el) return;
      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollLeft = Math.min(mobileTabsScrollLeftRef.current, maxLeft);
      updateMobileTabOverflowHints();
    };
    restoreMobile();
    requestAnimationFrame(restoreMobile);
  }, [activeTab, updateMobileTabOverflowHints]);

  useLayoutEffect(() => {
    if (activeTab !== 'curriculum') return;

    const restore = () => {
      const beltEl = beltPickerScrollRef.current;
      if (beltEl) {
        const maxBelt = Math.max(0, beltEl.scrollWidth - beltEl.clientWidth);
        beltEl.scrollLeft = Math.min(beltPickerScrollLeftRef.current, maxBelt);
        updateBeltPickerOverflowHints();
      }
      const navEl = curriculumNavScrollRef.current;
      if (navEl) {
        const maxNav = Math.max(0, navEl.scrollWidth - navEl.clientWidth);
        navEl.scrollLeft = Math.min(curriculumNavScrollLeftRef.current, maxNav);
        updateCurriculumNavOverflowHints();
      }
    };

    restore();
    requestAnimationFrame(restore);
  }, [activeTab, updateBeltPickerOverflowHints, updateCurriculumNavOverflowHints]);

  /** Belt / subject strip width changes — clamp scroll, do not restore from saved refs (those apply only when re-entering Curriculum). */
  useLayoutEffect(() => {
    if (activeTab !== 'curriculum') return;

    const run = () => {
      updateBeltPickerOverflowHints();
      updateCurriculumNavOverflowHints();
    };

    run();
    requestAnimationFrame(run);
  }, [
    activeTab,
    selectedBelt,
    visibleCurriculumCategories,
    updateBeltPickerOverflowHints,
    updateCurriculumNavOverflowHints,
  ]);

  useEffect(() => {
    const updateBackToTopVisibility = () => {
      const isMobileViewport = window.innerWidth < 768;
      const scrolledPastThreshold = window.scrollY > window.innerHeight * 2;
      setShowBackToTop(isMobileViewport && scrolledPastThreshold);
    };

    updateBackToTopVisibility();
    window.addEventListener('scroll', updateBackToTopVisibility, { passive: true });
    window.addEventListener('resize', updateBackToTopVisibility);

    return () => {
      window.removeEventListener('scroll', updateBackToTopVisibility);
      window.removeEventListener('resize', updateBackToTopVisibility);
    };
  }, []);

  // Lock viewport overscroll only while the video dialog is open. Applying this
  // for the whole Members visit breaks Chrome Android pull-to-refresh.
  useEffect(() => {
    if (!isVideoDialogOpen) return;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverscroll = html.style.overscrollBehaviorY;
    const previousBodyOverscroll = body.style.overscrollBehaviorY;

    html.style.overscrollBehaviorY = 'none';
    body.style.overscrollBehaviorY = 'none';

    return () => {
      html.style.overscrollBehaviorY = previousHtmlOverscroll;
      body.style.overscrollBehaviorY = previousBodyOverscroll;
    };
  }, [isVideoDialogOpen]);
  
  return (
    <PageLayout>
      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">Welcome, {profile?.first_name}!</h1>
            <p className="text-xl text-gray-300">
              This is the exclusive members area for OMAC-UK Taekwondo students. 
              Here you'll find resources, training materials, and members-only content.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4">
        <SectionHeading        
              title="Members Area"
              gradient={true}
            />
    
        <div className="bg-white shadow-md rounded-lg">
          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={handleMembersTabChange} className="w-full">
            <div className="md:hidden relative border-b bg-white/95 backdrop-blur">
              <div
                ref={mobileTabsScrollRef}
                onScroll={updateMobileTabOverflowHints}
                className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                <TabsList className="inline-flex h-auto min-w-full justify-start gap-2 rounded-full bg-gray-100 p-1">
                  {memberTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm"
                    >
                      <span>{tab.mobileLabel}</span>
                      {tab.value === 'upcoming-events' &&
                        remainingEventsThisMonthCount != null &&
                        remainingEventsThisMonthCount > 0 && (
                          <span
                            className="font-semibold tabular-nums text-green-600"
                            aria-label={`${remainingEventsThisMonthCount} upcoming events this month`}
                          >
                            ({remainingEventsThisMonthCount})
                          </span>
                        )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {showMobileTabsLeftHint && (
                <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-16 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
                  <button
                    type="button"
                    className="pointer-events-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-taekwondo-red/20 bg-white/95 shadow-md shadow-taekwondo-red/10 ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-taekwondo-red/40"
                    aria-label="Scroll members tabs left"
                    onClick={() => scrollHorizontalTabStrip(mobileTabsScrollRef, "backward")}
                  >
                    <ChevronsLeft className="h-6 w-6 text-taekwondo-red" />
                  </button>
                </div>
              )}
              {showMobileTabsRightHint && (
                <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-16 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
                  <button
                    type="button"
                    className="pointer-events-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-taekwondo-red/20 bg-white/95 shadow-md shadow-taekwondo-red/10 ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-taekwondo-red/40"
                    aria-label="Scroll members tabs right"
                    onClick={() => scrollHorizontalTabStrip(mobileTabsScrollRef, "forward")}
                  >
                    <ChevronsRight className="h-6 w-6 text-taekwondo-red" />
                  </button>
                </div>
              )}
            </div>

            <div className="hidden md:block">
              <TabsList className="mb-6">
                {memberTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="inline-flex items-center gap-1.5"
                  >
                    <span>{tab.label}</span>
                    {tab.value === 'upcoming-events' &&
                      remainingEventsThisMonthCount != null &&
                      remainingEventsThisMonthCount > 0 && (
                        <span
                          className="font-semibold tabular-nums text-green-600"
                          aria-label={`${remainingEventsThisMonthCount} upcoming events this month`}
                        >
                          ({remainingEventsThisMonthCount})
                        </span>
                      )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="curriculum">
              <div className="p-4 md:p-6">
                <h2 className="text-2xl font-semibold mb-6 text-taekwondo-red">Taekwondo Curriculum</h2>
                
                <div>
                  <p className="block text-lg font-medium mb-3">Select Current Belt</p>
                  <div className="relative">
                    <div
                      ref={beltPickerScrollRef}
                      onScroll={updateBeltPickerOverflowHints}
                      className="touch-pan-x overflow-x-auto py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                      <div className="inline-flex gap-3 pr-2">
                        {beltOptions.map((belt) => {
                          const isSelected = selectedBelt === belt.value;

                          return (
                            <button
                              key={belt.value}
                              type="button"
                              onClick={() => setSelectedBelt(belt.value)}
                              className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 bg-white transition-all md:h-16 md:w-16 ${
                                isSelected
                                  ? 'border-taekwondo-blue shadow-md shadow-taekwondo-blue/20 ring-2 ring-taekwondo-blue/20'
                                  : 'border-gray-200 hover:border-taekwondo-blue/40'
                              }`}
                              aria-pressed={isSelected}
                              aria-label={`Select ${belt.label} belt`}
                              title={belt.value}
                            >
                              <img
                                src={belt.image}
                                alt={belt.label}
                                className="max-h-8 w-auto object-contain md:max-h-10"
                                loading="lazy"
                              />
                              {belt.badge && (
                                <span className="absolute -bottom-1 -right-1 rounded-full bg-taekwondo-blue px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                                  {belt.badge}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {showBeltPickerLeftHint && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-16 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-1">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-taekwondo-blue/20 bg-white/95 shadow-md shadow-taekwondo-blue/10 ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-taekwondo-blue/40"
                          aria-label="Scroll belt list left"
                          onClick={() => scrollHorizontalTabStrip(beltPickerScrollRef, "backward")}
                        >
                          <ChevronsLeft className="h-6 w-6 text-taekwondo-blue" />
                        </button>
                      </div>
                    )}
                    {showBeltPickerRightHint && (
                      <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-16 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-1">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-taekwondo-blue/20 bg-white/95 shadow-md shadow-taekwondo-blue/10 ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-taekwondo-blue/40"
                          aria-label="Scroll belt list right"
                          onClick={() => scrollHorizontalTabStrip(beltPickerScrollRef, "forward")}
                        >
                          <ChevronsRight className="h-6 w-6 text-taekwondo-blue" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    Current belt: <span className="font-semibold text-taekwondo-blue">{selectedBelt}</span>
                  </p>
                </div>

                <hr className="my-8 border-0 border-t border-gray-200" />

                <div className="mb-8">
                  <p className="block text-lg font-medium mb-1">Select Subject</p>
                  <CurriculumSubjectNavSyncContext.Provider value={scrollActiveCurriculumSubjectIntoView}>
                  <Tabs
                    value={activeCurriculumCategory}
                    onValueChange={setActiveCurriculumCategory}
                    className="w-full space-y-4"
                  >
                  <div className="relative border-b bg-white/95 backdrop-blur md:border-0 md:bg-transparent">
                    <div
                      ref={curriculumNavScrollRef}
                      onScroll={updateCurriculumNavOverflowHints}
                      className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                      <TabsList className="inline-flex h-auto w-max min-w-0 flex-nowrap justify-start gap-2 rounded-full bg-gray-100 p-1">
                        {visibleCurriculumCategories.map((c) => (
                          <TabsTrigger
                            key={c.value}
                            value={c.value}
                            data-curriculum-subject-tab
                            className={cn(
                              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium border border-black/10 transition-shadow",
                              getCurriculumTabBeltSurface(selectedBelt),
                              "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                            )}
                          >
                            <span className="md:hidden">{c.mobileLabel}</span>
                            <span className="hidden md:inline">{c.label}</span>
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                    {showCurriculumNavLeftHint && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-14 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                          aria-label="Scroll curriculum tabs left"
                          onClick={() => scrollHorizontalTabStrip(curriculumNavScrollRef, "backward")}
                        >
                          <ChevronsLeft className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    )}
                    {showCurriculumNavRightHint && (
                      <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-14 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                          aria-label="Scroll curriculum tabs right"
                          onClick={() => scrollHorizontalTabStrip(curriculumNavScrollRef, "forward")}
                        >
                          <ChevronsRight className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    )}
                  </div>

                  {visibleCurriculumCategories.map((c) => (
                    <TabsContent key={c.value} value={c.value} className="mt-0 focus-visible:outline-none">
                      {c.value === "non-combat" ? (
                        <CurriculumNonCombatTheory
                          selectedBelt={selectedBelt}
                          theoryContent={theoryContent}
                          applicableOralVerses={applicableOralVerses}
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                        />
                      ) : c.value === "geukki" ? (
                        <div className="text-left">
                          <h3 className="mb-3 font-semibold text-lg text-taekwondo-red">
                            Geukki (Kicks)
                          </h3>
                          <div className="rounded-lg bg-gray-50 p-4">
                            <ul className="list-inside list-disc space-y-2 text-gray-700">
                              {getKicks(selectedBelt).map((kick, index) => (
                                <li key={index}>{kick}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : c.value === "sparring" ? (
                        <Sparring
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          breaking={getBreakingTechniques(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                        />
                      ) : c.value === "one-step" ? (
                        <OneStep
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          requirements={getOneStepRequirements(selectedBelt)}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                        />
                      ) : c.value === "ho-shin-sul" ? (
                        <HoShinSul
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          techniques={getHoShinSulTechniques(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                        />
                      ) : c.value === "mooshim" ? (
                        <CurriculumMooshim
                          techniques={getMooshimTechniques(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "innae" ? (
                        <Innae
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          techniques={getInnaeTechniques(selectedBelt)}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "taegeuk" ? (
                        <Taeguek
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          patterns={patterns}
                          currentPattern={currentPattern}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "moogongryu" ? (
                        <CurriculumMooGongRyu
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          showDaeMahngChun={isDaeMahngChunVisible(selectedBelt)}
                          showDaeMahngJi={isDaeMahngJiVisible(selectedBelt)}
                          showChoshim={isChoshimVisible(selectedBelt)}
                          showJoHwa={isJoHwaVisible(selectedBelt)}
                          showGyeomJeon={isGyeomJeonVisible(selectedBelt)}
                          showPalBahngMooGong={isPalBahngMooGongVisible(selectedBelt)}
                          showSongMoo={isSongMooVisible(selectedBelt)}
                          showSaengMyongEum={isSaengMyongEumVisible(selectedBelt)}
                          showSaengMyongYang={isSaengMyongYangVisible(selectedBelt)}
                          dmcPatterns={dmcPatterns}
                          currentDMC={currentDMC}
                          dmjPatterns={dmjPatterns}
                          currentDMJ={currentDMJ}
                        />
                      ) : c.value === "songmookwan" ? (
                        <CurriculumSongMooKwan
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          showChulGi={isChulGiVisible(selectedBelt)}
                          showRohai={isRohaiVisible(selectedBelt)}
                          showBalSaeDae={isBalSaeDaeVisible(selectedBelt)}
                          showBalSeakSo={isBalSeakSoVisible(selectedBelt)}
                          showYeonBi={isYeonBiVisible(selectedBelt)}
                          showAhmHak={isAhmHakVisible(selectedBelt)}
                          showSoHoYeon={isSoHoYeonVisible(selectedBelt)}
                          showJaEun={isJaEunVisible(selectedBelt)}
                          showKeumKangKwon={isKeumKangKwonVisible(selectedBelt)}
                        />
                      ) : c.value === "kukkiwon-wt" ? (
                        <CurriculumKukkiwon
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          showKoryo={isKoryoVisible(selectedBelt)}
                          showKeumgang={isKeumgangVisible(selectedBelt)}
                          showTaebaek={isTaebaekVisible(selectedBelt)}
                          showPyongWon={isPyongWonVisible(selectedBelt)}
                          showSipJin={isSipJinVisible(selectedBelt)}
                          showJitae={isJitaeVisible(selectedBelt)}
                        />
                      ) : c.value === "choongmoo" ? (
                        <ChoongMoo
                          selectedBelt={selectedBelt}
                          getBeltColor={getBeltColor}
                          hasDemo={hasTrainingVideo}
                          onWatchDemo={handleWatchTrainingVideo}
                          presentation="curriculum"
                          beltTabSurfaceClass={getCurriculumTabBeltSurface(selectedBelt)}
                        />
                      ) : (
                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/90 px-4 py-10 text-center md:px-6">
                          <p className="text-lg font-semibold text-gray-900">
                            {(c as { label: string }).label}
                          </p>
                          <p className="mt-2 text-sm text-gray-600">
                            Tab active — content for this section will replace this placeholder.
                          </p>
                        </div>
                      )}
                    </TabsContent>
                  ))}
                  </Tabs>
                  </CurriculumSubjectNavSyncContext.Provider>
                </div>

              </div>
            </TabsContent>

            {/* Lil Dragons Tab */}
            <TabsContent value="lil-dragons">
              <LilDragonsTab
                hasTrainingVideo={hasTrainingVideo}
                handleWatchTrainingVideo={handleWatchTrainingVideo}
              />
            </TabsContent>
            
            {/* Korean Words Tab */}
            <TabsContent value="korean-words">
              <MembersKoreanWordsTab />
            </TabsContent>

            {/* Upcoming Events Tab */}
            <TabsContent value="upcoming-events">
              <MembersUpcomingEventsTab />
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              <MembersVideosTab
                isLoading={isLoadingTrainingVideos}
                error={trainingVideosError}
                videos={trainingVideos}
                videoTabBucketByMediaId={trainingVideoTabBucketByMediaId}
                onSelectVideo={(video) => {
                  setActiveVideo(video);
                  setIsVideoDialogOpen(true);
                }}
              />
            </TabsContent>

            {/* Forms Tab */}
            <TabsContent value="forms">
              <MembersFormsTab />
            </TabsContent>
         
            {/* Media Tab */}
            <TabsContent value="media">
              <MembersMediaTab />
            </TabsContent>

            {/* Merchandise Tab */}        
            <TabsContent value="merchandise">
              <MembersMerchandiseTab />
            </TabsContent>

          </Tabs>

          {/* Global video preview dialog so it works from any tab (e.g. Curriculum) */}
          <Dialog
            open={isVideoDialogOpen && !!activeVideo}
            onOpenChange={(open) => {
              setIsVideoDialogOpen(open);
              if (!open) {
                setActiveVideo(null);
              }
            }}
          >
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {activeVideo ? getTrainingVideoListLabel(activeVideo) : null}
                </DialogTitle>
              </DialogHeader>
              {activeVideo && (
                <div className="mt-2">
                  <video
                    src={activeVideo.url}
                    controls
                    className="w-full max-h-[70vh] rounded-md bg-black"
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-5 right-4 z-50 h-11 rounded-full bg-taekwondo-blue px-4 text-white shadow-lg transition-all duration-300 hover:bg-taekwondo-blue/90 md:hidden ${
          showBackToTop
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-3 opacity-0'
        }`}
        aria-hidden={!showBackToTop}
        tabIndex={showBackToTop ? 0 : -1}
      >
        <ArrowUp className="mr-2 h-4 w-4" />
        Back to top
      </Button>
    </PageLayout>
  );
}
